import { supabase } from '../lib/supabase';
import { subscriptionService } from './subscriptionService';

/**
 * Email Notification Service
 * Handles all email notifications for job applications
 * Uses Resend via Supabase Edge Functions
 */

// ============================================
// EMAIL SENDING FUNCTIONS
// ============================================

/**
 * Base function to send emails via Edge Function
 * @param {string} emailType - Type of email to send
 * @param {Object} data - Email data
 * @returns {Promise<Object>} - Send result
 */
const sendEmail = async (emailType, data) => {
  try {
    const { data: result, error } = await supabase?.functions?.invoke('send-application-email', {
      body: { emailType, data }
    });

    if (error) throw error;

    return {
      success: true,
      messageId: result?.messageId,
      emailType
    };
  } catch (error) {
    console.error(`Error sending ${emailType} email:`, error);
    throw new Error(`Failed to send email: ${error.message}`);
  }
};

/**
 * Sends application received notification to company
 * Differentiates between freemium and paid plans
 * @param {Object} applicationData - Application details
 * @returns {Promise<Object>} - Send result
 */
export const sendApplicationReceivedEmail = async (applicationData) => {
  try {
    // Get company subscription plan
    const subscriptionResult = await subscriptionService?.getCurrentSubscription(applicationData?.companyId);
    const plan = subscriptionResult?.data?.plan || 'freemium';
    const isFreemium = plan === 'freemium';

    const emailData = {
      companyEmail: applicationData?.companyEmail,
      companyName: applicationData?.companyName,
      jobTitle: applicationData?.jobTitle,
      candidateName: isFreemium ? 'Hidden (Freemium Plan)' : applicationData?.candidateName,
      candidateEmail: isFreemium ? 'Hidden (Freemium Plan)' : applicationData?.candidateEmail,
      coverLetter: isFreemium ? null : applicationData?.coverLetter,
      appliedAt: applicationData?.appliedAt,
      dashboardUrl: `${window.location?.origin}/company-dashboard-hub/${applicationData?.companyId}/candidates?applicationId=${applicationData?.applicationId}`,
      isFreemium,
      plan
    };

    return await sendEmail('APPLICATION_RECEIVED_COMPANY', emailData);
  } catch (error) {
    console.error('Error sending application received email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends application confirmation to candidate
 * @param {Object} applicationData - Application details
 * @returns {Promise<Object>} - Send result
 */
export const sendApplicationConfirmationEmail = async (applicationData) => {
  try {
    const emailData = {
      candidateEmail: applicationData?.candidateEmail,
      candidateName: applicationData?.candidateName,
      jobTitle: applicationData?.jobTitle,
      companyName: applicationData?.companyName,
      applicationId: applicationData?.applicationId,
      appliedAt: applicationData?.appliedAt,
      dashboardUrl: `${window.location?.origin}/job-seeker-dashboard?tab=applications&applicationId=${applicationData?.applicationId}`
    };

    return await sendEmail('APPLICATION_CONFIRMATION_CANDIDATE', emailData);
  } catch (error) {
    console.error('Error sending application confirmation email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends status update notification to candidate
 * @param {Object} statusUpdateData - Status update details
 * @returns {Promise<Object>} - Send result
 */
export const sendStatusUpdateEmailToCandidate = async (statusUpdateData) => {
  try {
    const emailData = {
      candidateEmail: statusUpdateData?.candidateEmail,
      candidateName: statusUpdateData?.candidateName,
      jobTitle: statusUpdateData?.jobTitle,
      companyName: statusUpdateData?.companyName,
      newStatus: statusUpdateData?.newStatus,
      notes: statusUpdateData?.notes || null,
      updatedAt: statusUpdateData?.updatedAt,
      dashboardUrl: `${window.location?.origin}/job-seeker-dashboard?tab=applications&applicationId=${statusUpdateData?.applicationId}`
    };

    return await sendEmail('STATUS_UPDATE_CANDIDATE', emailData);
  } catch (error) {
    console.error('Error sending status update email to candidate:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends status update notification to company
 * @param {Object} statusUpdateData - Status update details
 * @returns {Promise<Object>} - Send result
 */
export const sendStatusUpdateEmailToCompany = async (statusUpdateData) => {
  try {
    const emailData = {
      companyEmail: statusUpdateData?.companyEmail,
      companyName: statusUpdateData?.companyName,
      candidateName: statusUpdateData?.candidateName,
      jobTitle: statusUpdateData?.jobTitle,
      oldStatus: statusUpdateData?.oldStatus,
      newStatus: statusUpdateData?.newStatus,
      updatedBy: statusUpdateData?.updatedBy,
      dashboardUrl: `${window.location?.origin}/company-dashboard-hub?tab=candidates&applicationId=${statusUpdateData?.applicationId}`
    };

    return await sendEmail('STATUS_UPDATE_COMPANY', emailData);
  } catch (error) {
    console.error('Error sending status update email to company:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends daily digest email to company with multiple applications
 * @param {Object} digestData - Digest data
 * @returns {Promise<Object>} - Send result
 */
export const sendDailyDigestEmail = async (digestData) => {
  try {
    const emailData = {
      companyEmail: digestData?.companyEmail,
      companyName: digestData?.companyName,
      applications: digestData?.applications,
      totalPending: digestData?.totalPending || 0,
      dashboardUrl: `${window.location?.origin}/company-dashboard-hub?tab=candidates`
    };

    return await sendEmail('DAILY_DIGEST_COMPANY', emailData);
  } catch (error) {
    console.error('Error sending daily digest email:', error);
    return { success: false, error: error?.message };
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Fetches user email from user_profiles
 * @param {string} userId - User ID
 * @returns {Promise<string>} - User email
 */
export const getUserEmail = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('email')?.eq('id', userId)?.single();

    if (error) throw error;

    return data?.email;
  } catch (error) {
    console.error('Error fetching user email:', error);
    throw new Error('Failed to fetch user email');
  }
};

/**
 * Fetches company email from companies table
 * @param {string} companyId - Company ID
 * @returns {Promise<string>} - Company email
 */
export const getCompanyEmail = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('companies')?.select('contact_email')?.eq('id', companyId)?.single();

    if (error) throw error;

    return data?.contact_email;
  } catch (error) {
    console.error('Error fetching company email:', error);
    throw new Error('Failed to fetch company email');
  }
};

/**
 * Sends both application emails (company + candidate) automatically
 * @param {Object} applicationData - Complete application data
 * @returns {Promise<Object>} - Combined result
 */
export const sendApplicationNotifications = async (applicationData) => {
  try {
    // Send both emails in parallel
    const [companyResult, candidateResult] = await Promise.allSettled([
      sendApplicationReceivedEmail(applicationData),
      sendApplicationConfirmationEmail(applicationData)
    ]);

    return {
      success: true,
      results: {
        companyEmail: companyResult?.status === 'fulfilled' ? companyResult?.value : { success: false, error: companyResult?.reason },
        candidateEmail: candidateResult?.status === 'fulfilled' ? candidateResult?.value : { success: false, error: candidateResult?.reason }
      }
    };
  } catch (error) {
    console.error('Error sending application notifications:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Sends status update emails to both parties
 * @param {Object} statusUpdateData - Complete status update data
 * @returns {Promise<Object>} - Combined result
 */
export const sendStatusUpdateNotifications = async (statusUpdateData) => {
  try {
    // Send both emails in parallel
    const [candidateResult, companyResult] = await Promise.allSettled([
      sendStatusUpdateEmailToCandidate(statusUpdateData),
      sendStatusUpdateEmailToCompany(statusUpdateData)
    ]);

    return {
      success: true,
      results: {
        candidateEmail: candidateResult?.status === 'fulfilled' ? candidateResult?.value : { success: false, error: candidateResult?.reason },
        companyEmail: companyResult?.status === 'fulfilled' ? companyResult?.value : { success: false, error: companyResult?.reason }
      }
    };
  } catch (error) {
    console.error('Error sending status update notifications:', error);
    return { success: false, error: error?.message };
  }
};

// Export all functions
export default {
  sendApplicationReceivedEmail,
  sendApplicationConfirmationEmail,
  sendStatusUpdateEmailToCandidate,
  sendStatusUpdateEmailToCompany,
  sendDailyDigestEmail,
  sendApplicationNotifications,
  sendStatusUpdateNotifications,
  getUserEmail,
  getCompanyEmail
};
export { sendEmail };